import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { BrandButton } from "@/components/kit/BrandButton";
import { track } from "@/lib/analytics";

const DISMISSED = "ppcp_install_dismissed";

/** True once the app is running as an installed PWA, so an installed device never sees the card. */
const installed = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/**
 * The browser's own install prompt, surfaced as a card.
 *
 * Renders nothing unless the browser has actually offered `beforeinstallprompt` — so a device that
 * cannot install (iOS Safari, an already-installed app) sees no button rather than a dead one. The
 * offer is captured in index.html, before React mounts, because the event fires early in page load
 * and is not replayed: a listener attached at mount misses it on a reload.
 */
export const InstallAppCard = () => {
  const [prompt, setPrompt] = useState(window.__ppcpInstallPrompt ?? null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED) === "1");

  useEffect(() => {
    if (installed()) return undefined;
    const offer = (event) => {
      event.preventDefault(); // Keep the event so the card's button can replay it later.
      setPrompt(event);
    };
    const done = () => setPrompt(null);
    window.addEventListener("beforeinstallprompt", offer);
    window.addEventListener("appinstalled", done);
    return () => {
      window.removeEventListener("beforeinstallprompt", offer);
      window.removeEventListener("appinstalled", done);
    };
  }, []);

  if (!prompt || dismissed) return null;

  const install = async () => {
    setPrompt(null); // A prompt can only be used once, whatever the user chooses.
    window.__ppcpInstallPrompt = null;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    track("pwa_install_prompt", { outcome });
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED, "1"); // Declining is remembered; we do not ask again.
    setDismissed(true);
    track("pwa_install_dismissed");
  };

  return (
    <div
      data-testid="install-app-card"
      className="relative rounded-[20px] border border-brand-teal/10 bg-card p-4 shadow-card sm:p-5"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="install-app-dismiss"
        className="ps-tap absolute right-2.5 top-2.5 grid h-8 w-8 place-items-center rounded-full text-brand-teal/40 transition-colors hover:bg-secondary hover:text-brand-teal"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-4 pr-8">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-teal/[0.07] text-brand-teal">
          <Download className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[15px] font-semibold text-brand-teal">
            Install Plattershare Meal Plans
          </p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">
            Add Meal Plans to your device for quick access.
          </p>
          <BrandButton name="install-app" size="sm" className="mt-4" onClick={install}>
            Install App
          </BrandButton>
        </div>
      </div>
    </div>
  );
};
